import React from 'react'
import type { ColorBandBlock as BlockProps } from '@/payload-types'
import { RenderBlocks } from '@/blocks/RenderBlocks'
import { cn } from '@/utilities/cn'

const VARIANT_CLASS: Record<string, string> = {
  white: 'bg-white text-slate-900',
  muted: 'bg-slate-50 text-slate-900',
  slate: 'bg-slate-100 text-slate-900',
  primary: 'bg-primary/10 text-slate-900',
  ocean: 'bg-ocean-blue/10 text-slate-900',
  accent: 'bg-accent/30 text-slate-900',
  gradient: 'bg-gradient-to-br from-primary/20 via-white to-ocean-blue/20 text-slate-900',
  dark: 'bg-slate-900 text-white',
}

const PADDING_Y_CLASS: Record<string, string> = {
  none: 'py-0',
  sm: 'py-4',
  md: 'py-8 md:py-12',
  lg: 'py-12 md:py-20',
  xl: 'py-20 md:py-32',
}

export const ColorBandBlock: React.FC<BlockProps> = ({
  layout,
  variant,
  paddingY,
  fullBleed,
}) => {
  // Full-bleed escape: when nested inside a constrained-width parent,
  // push the colored panel out to viewport edges with `vw`-based margins.
  // `-mx-[50vw]` + `w-screen` does the work; `left-1/2` re-centers since
  // the negative margin is symmetric. (We don't set `right`: with
  // `position: relative`, browsers give `left` precedence.)
  //
  // Caveat: this requires an ancestor with `overflow-x: hidden` (or
  // `overflow-x: clip`) — otherwise `-50vw` margins create a horizontal
  // scrollbar. The frontend layout sets `body { overflow-x: hidden }` in
  // src/app/(frontend)/globals.scss, but anyone using ColorBand inside a
  // custom shell (embed, auth route group, etc.) needs to verify it.
  const escape = fullBleed
    ? 'relative left-1/2 -mx-[50vw] w-screen'
    : ''
  return (
    <div className={cn(VARIANT_CLASS[variant || 'muted'], PADDING_Y_CLASS[paddingY || 'lg'], escape)}>
      {/* When full-bleed, re-constrain the inner content so text doesn't
          run edge-to-edge on wide viewports. `max-w-7xl` (1280px) matches
          the `wide` preset in `src/components/Container` — the widest
          regular content width in the design system, so the panel's
          inner content lines up with surrounding non-bleed sections. */}
      <div className={fullBleed ? 'mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8' : ''}>
        <RenderBlocks blocks={layout ?? []} />
      </div>
    </div>
  )
}
